import { create } from 'zustand';
import { CHECKOUT_LIST_SUBSCRIPTION_STATUSES } from 'lib/billing/subscriptionStatuses';
import { buildPriceCatalog } from 'lib/billing/priceCatalog';
import { overviewFromPb, pricesFromPb, subscriptionsFromPb, type LibSubscription } from 'lib/billing/pbAdapter';
import { analytics } from 'lib/analytics';
import type { BillingOverviewReply, ListPricesReply, ListSubscriptionsReply } from 'proto/billing-manager_pb';
import type { BillingActions, BillingClientFactory, BillingState } from './types';
import { BILLING_CACHE_STALE_MS, BILLING_CATALOG_STALE_MS } from './cacheConfig';

interface InternalState {
  clientFactory: BillingClientFactory | null;
  inflight: Promise<void> | null;
  inflightCatalog: Promise<void> | null;
  lastCatalogFetched: number | null;
}

interface Wiring {
  setClientFactory: (factory: BillingClientFactory | null) => void;
}

type Store = BillingState & BillingActions & InternalState & Wiring;

const INITIAL_STATE: BillingState & InternalState = {
  subscriptions: [],
  overview: null,
  catalog: null,
  loading: false,
  catalogLoading: false,
  error: null,
  catalogError: null,
  lastFetched: null,
  clientFactory: null,
  inflight: null,
  inflightCatalog: null,
  lastCatalogFetched: null
};

// Monotonic tokens for fetch supersedure. A `force: true` request bumps the
// token and starts a new request; any earlier in-flight request that resolves
// later checks its token before writing and bails if superseded. Without this,
// a fetch fired right after a Stripe confirm (before the webhook lands) would
// resolve with stale data and overwrite a fresher post-webhook fetch.
let billingFetchToken = 0;
let catalogFetchToken = 0;

/** The stored subscription with this id, or undefined until billing has loaded it. */
export const findStoredSubscription = (subscriptions: unknown, id?: string | null): LibSubscription | undefined =>
  id ? ((subscriptions as LibSubscription[] | undefined) || []).find((s) => s.id === id) : undefined;

export const useBillingStore = create<Store>()((set, get) => ({
  ...INITIAL_STATE,

  setClientFactory: (clientFactory) => set({ clientFactory }),

  fetch: async (userId, opts) => {
    const { clientFactory, inflight, lastFetched } = get();
    if (!clientFactory || !userId) return;
    const force = opts?.force === true;
    if (!force && inflight) return inflight;
    if (!force) {
      const fresh = lastFetched !== null && Date.now() - lastFetched < BILLING_CACHE_STALE_MS;
      if (fresh) return;
    }

    billingFetchToken += 1;
    const myToken = billingFetchToken;

    const run = (async () => {
      set({ loading: true, error: null });
      try {
        const client = await clientFactory();
        const [subsReply, overviewReply] = await Promise.all([
          client.listSubscriptions({
            userId,
            statusList: [...CHECKOUT_LIST_SUBSCRIPTION_STATUSES]
          }),
          client.billingOverview({ userId })
        ]);
        if (myToken !== billingFetchToken) return;
        const subscriptions = subscriptionsFromPb(subsReply as ListSubscriptionsReply.AsObject);
        set({
          subscriptions,
          overview: overviewFromPb(overviewReply as BillingOverviewReply.AsObject),
          loading: false,
          lastFetched: Date.now(),
          inflight: null
        });
        // The one place the customer's live subscriptions are known in full.
        analytics.setSubscriptionSummary(subscriptions);
      } catch (error) {
        if (myToken !== billingFetchToken) return;
        set({
          subscriptions: [],
          overview: null,
          loading: false,
          error,
          inflight: null
        });
      }
    })();

    set({ inflight: run });
    return run;
  },

  fetchCatalog: async (userId) => {
    const { clientFactory, inflightCatalog, lastCatalogFetched } = get();
    if (!clientFactory || !userId) return;
    if (inflightCatalog) return inflightCatalog;
    const fresh = lastCatalogFetched !== null && Date.now() - lastCatalogFetched < BILLING_CATALOG_STALE_MS;
    if (fresh) return;

    catalogFetchToken += 1;
    const myToken = catalogFetchToken;

    const run = (async () => {
      set({ catalogLoading: true, catalogError: null });
      try {
        const client = await clientFactory();
        const pricesReply = await client.listPrices({ userId });
        if (myToken !== catalogFetchToken) return;
        set({
          catalog: buildPriceCatalog(pricesFromPb(pricesReply as ListPricesReply.AsObject)),
          catalogLoading: false,
          lastCatalogFetched: Date.now(),
          inflightCatalog: null
        });
      } catch (catalogError) {
        if (myToken !== catalogFetchToken) return;
        set({
          catalog: null,
          catalogLoading: false,
          catalogError,
          inflightCatalog: null
        });
      }
    })();

    set({ inflightCatalog: run });
    return run;
  },

  invalidate: () => set({ lastFetched: null }),

  applySubscriptionUpdate: (sub) =>
    set((s) => {
      const list = s.subscriptions as Array<{ id: string }>;
      const idx = list.findIndex((x) => x?.id === sub.id);
      if (idx === -1) {
        return { subscriptions: [...list, sub] };
      }
      const next = list.slice();
      next[idx] = { ...next[idx], ...sub };
      return { subscriptions: next };
    }),

  reset: () => set(INITIAL_STATE)
}));
